/**
 * Cycle Readiness Store
 * 
 * Calls the evaluate_cycle_readiness() Supabase RPC to determine whether
 * prerequisite conditions for S6 (Item Prioritization) are satisfied.
 * 
 * Prerequisites are CONDITIONS, not a mandated order:
 * - Participation locked
 * - Criteria locked
 * - Pool locked
 * - Values sufficient
 * 
 * These can be satisfied in any order. When all are met, S6 can run.
 */

import { create } from 'zustand';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import type {
  CycleReadinessResult,
  ReadinessBlocker,
  TransitionKey,
} from '../../types/prioritization/readiness';

interface CycleReadinessState {
  readiness: Record<string, CycleReadinessResult | null>;
  isLoading: Record<string, boolean>;
  error: Record<string, string | null>;
  lastEvaluated: Record<string, string | null>;
  
  // Actions
  evaluateReadiness: (cycleId: string) => Promise<CycleReadinessResult | null>;
  clearReadiness: (cycleId: string) => void;
  canAdvance: (cycleId: string, transition: TransitionKey) => boolean;
  getBlockers: (cycleId: string, transition: TransitionKey) => ReadinessBlocker[];
  getNextTransition: (cycleId: string) => TransitionKey | null;
  getError: (cycleId: string) => string | null;
  isEvaluating: (cycleId: string) => boolean;
}

// ============================================================================
// Store
// ============================================================================

export const useCycleReadinessStore = create<CycleReadinessState>((set, get) => ({
  readiness: {},
  isLoading: {},
  error: {},
  lastEvaluated: {},
  
  evaluateReadiness: async (cycleId: string) => {
    set(state => ({
      isLoading: { ...state.isLoading, [cycleId]: true },
      error: { ...state.error, [cycleId]: null },
    }));
    
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase client not available');
      }
      
      const { data, error } = await supabase
        .rpc('evaluate_cycle_readiness', { p_cycle_id: cycleId });
      
      if (error) {
        throw error;
      }
      
      const evaluatedAt = data?.evaluated_at ?? new Date().toISOString();
      const result: CycleReadinessResult = {
        cycle_id: cycleId,
        current_phase: data?.current_phase ?? 'ENGAGE',
        next_transition: data?.next_transition ?? null,
        evaluated_at: evaluatedAt,
        by_transition: {
          D_to_E: data?.by_transition?.D_to_E ?? { pass: false, blockers: [] },
          E_to_G: data?.by_transition?.E_to_G ?? { pass: false, blockers: [] },
          G_to_A: data?.by_transition?.G_to_A ?? { pass: false, blockers: [] },
          A_to_P: data?.by_transition?.A_to_P ?? { pass: false, blockers: [] },
        },
        summary: data?.summary ?? {
          can_enter_engage: false,
          can_enter_gather: false,
          can_enter_arrange: false,
          can_enter_prioritize: false,
          can_complete: false,
        },
      };
      
      set(state => ({
        readiness: { ...state.readiness, [cycleId]: result },
        isLoading: { ...state.isLoading, [cycleId]: false },
        lastEvaluated: { ...state.lastEvaluated, [cycleId]: evaluatedAt },
      }));
      
      return result;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : (typeof err === 'object' && err && 'message' in err && typeof (err as { message?: unknown }).message === 'string'
              ? (err as { message: string }).message
              : 'Failed to evaluate readiness');
      set(state => ({
        isLoading: { ...state.isLoading, [cycleId]: false },
        error: { ...state.error, [cycleId]: errorMessage },
      }));
      console.error('evaluateReadiness error:', err);
      return null;
    }
  },
  
  clearReadiness: (cycleId: string) => {
    set(state => ({
      readiness: { ...state.readiness, [cycleId]: null },
      error: { ...state.error, [cycleId]: null },
      lastEvaluated: { ...state.lastEvaluated, [cycleId]: null },
    }));
  },

  canAdvance: (cycleId: string, transition: TransitionKey) => {
    if (get().error[cycleId]) return false;
    const readiness = get().readiness[cycleId];
    if (!readiness) return false;
    return readiness.by_transition[transition]?.pass === true;
  },

  getBlockers: (cycleId: string, transition: TransitionKey) => {
    const readiness = get().readiness[cycleId];
    if (!readiness) return [];
    return readiness.by_transition[transition]?.blockers ?? [];
  },

  getNextTransition: (cycleId: string) => {
    const readiness = get().readiness[cycleId];
    return readiness?.next_transition ?? null;
  },

  getError: (cycleId: string) => get().error[cycleId] ?? null,

  isEvaluating: (cycleId: string) => get().isLoading[cycleId] === true,
}));
